import { type FC, Suspense, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { MetaAdsLead } from "shared";
import { Icon } from "../common/Icon";
import { Card } from "../components/Card";
import { AppPage } from "../layout/AppLayout";
import { useLeadActions, useLeads } from "./useLeads";
import { useCanEditMetaAds } from "./useMyMetaAdsRole";

const PAGE_SIZE = 25;

function downloadCsv(leads: MetaAdsLead[]) {
	if (leads.length === 0) return;
	const allFieldKeys = new Set<string>();
	for (const lead of leads) {
		for (const key of Object.keys(lead.fields)) {
			allFieldKeys.add(key);
		}
	}
	const fieldKeys = Array.from(allFieldKeys);
	const headers = ["Date", "Form Name", ...fieldKeys];
	const rows = leads.map((lead) => [
		new Date(lead.createdTime).toLocaleString(),
		lead.formName,
		...fieldKeys.map((k) => lead.fields[k] ?? ""),
	]);

	const csvContent = [
		headers.join(","),
		...rows.map((row) =>
			row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
		),
	].join("\n");

	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = `leads-${new Date().toISOString().split("T")[0]}.csv`;
	link.click();
	URL.revokeObjectURL(url);
}

const LeadsTable: FC<{ formId?: string }> = ({ formId }) => {
	const { data } = useLeads(formId);
	const { syncLeads } = useLeadActions();
	const canEdit = useCanEditMetaAds();
	const [page, setPage] = useState(0);

	const leads = data.leads;
	const totalPages = Math.max(1, Math.ceil(leads.length / PAGE_SIZE));
	const pageLeads = leads.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

	const allFieldKeys = useMemo(() => {
		const keys = new Set<string>();
		for (const lead of leads) {
			for (const key of Object.keys(lead.fields)) {
				keys.add(key);
			}
		}
		return Array.from(keys);
	}, [leads]);

	if (leads.length === 0) {
		return (
			<Card>
				<Card.Header>
					Leads
					<div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
						{canEdit && (
							<button
								type="button"
								className="btn btn-sm btn-outline-primary"
								onClick={() => syncLeads.mutate()}
								disabled={syncLeads.isPending}
							>
								<Icon name="sync" size={14} />{" "}
								{syncLeads.isPending ? "Syncing..." : "Sync Now"}
							</button>
						)}
					</div>
				</Card.Header>
				<Card.Body>
					<p className="text-muted" style={{ marginBottom: 0 }}>
						No leads yet. Create a lead campaign to start collecting leads.
					</p>
				</Card.Body>
			</Card>
		);
	}

	return (
		<Card>
			<Card.Header>
				Leads ({data.totalCount})
				<div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
					{canEdit && (
						<button
							type="button"
							className="btn btn-sm btn-outline-primary"
							onClick={() => syncLeads.mutate()}
							disabled={syncLeads.isPending}
						>
							<Icon name="sync" size={14} />{" "}
							{syncLeads.isPending ? "Syncing..." : "Sync Now"}
						</button>
					)}
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						onClick={() => downloadCsv(leads)}
					>
						<Icon name="download" size={14} /> Export CSV
					</button>
				</div>
			</Card.Header>
			<Card.Body style={{ padding: 0 }}>
				<div style={{ overflowX: "auto" }}>
					<table className="table" style={{ marginBottom: 0 }}>
						<thead>
							<tr>
								<th>Date</th>
								<th>Form Name</th>
								<th>Campaign</th>
								<th>Ad</th>
								{allFieldKeys.map((key) => (
									<th key={key}>{key}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{pageLeads.map((lead) => (
								<tr key={lead.id}>
									<td>{new Date(lead.createdTime).toLocaleDateString()}</td>
									<td>{lead.formName}</td>
									<td>{lead.campaignName ?? "-"}</td>
									<td>{lead.adName ?? "-"}</td>
									{allFieldKeys.map((key) => (
										<td key={key}>{lead.fields[key] ?? "-"}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{totalPages > 1 && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							gap: 8,
							padding: 12,
						}}
					>
						<button
							type="button"
							className="btn btn-sm btn-outline-secondary"
							disabled={page === 0}
							onClick={() => setPage((p) => p - 1)}
						>
							Previous
						</button>
						<span style={{ alignSelf: "center" }}>
							Page {page + 1} of {totalPages}
						</span>
						<button
							type="button"
							className="btn btn-sm btn-outline-secondary"
							disabled={page >= totalPages - 1}
							onClick={() => setPage((p) => p + 1)}
						>
							Next
						</button>
					</div>
				)}
			</Card.Body>
		</Card>
	);
};

export const PageMetaAdsLeads: FC = () => {
	const [formFilter, setFormFilter] = useState<string | undefined>(undefined);

	return (
		<AppPage title="Leads">
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				<div style={{ display: "flex", gap: 12, alignItems: "center" }}>
					<label
						className="form-label"
						htmlFor="lead-form-filter"
						style={{ marginBottom: 0 }}
					>
						Filter by form:
					</label>
					<input
						id="lead-form-filter"
						type="text"
						className="form-control form-control-sm"
						style={{ maxWidth: 250 }}
						placeholder="Form ID (optional)"
						value={formFilter ?? ""}
						onChange={(e) => setFormFilter(e.target.value || undefined)}
					/>
				</div>

				<ErrorBoundary
					fallback={
						<Card>
							<Card.Body>
								<p className="text-muted" style={{ marginBottom: 0 }}>
									Unable to load leads.
								</p>
							</Card.Body>
						</Card>
					}
				>
					<Suspense fallback={<p>Loading leads...</p>}>
						<LeadsTable formId={formFilter} />
					</Suspense>
				</ErrorBoundary>
			</div>
		</AppPage>
	);
};
